import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { CheckCircle2, Calendar, Video } from 'lucide-react';
import { endOfMonth, format, startOfMonth, startOfToday } from 'date-fns';
import { createBooking, getSlots, SlotTakenError } from '../../services/calApi';
import { MonthGrid } from './MonthGrid';
import { SlotList, BookingState, TimeFormat } from './SlotList';

const TIMEZONE = 'Europe/London';
const EVENT_DURATION_MIN = 30;

interface Lead {
  name: string;
  email: string;
  organisation: string;
  role: string;
  message: string;
}

interface HeadlessBookerProps {
  lead: Lead;
  onLoaded?: () => void;
  onBooked?: () => void;
}

export const HeadlessBooker: React.FC<HeadlessBookerProps> = ({ lead, onLoaded, onBooked }) => {
  const [month, setMonth] = useState<Date>(() => startOfMonth(startOfToday()));
  const [slotsByDate, setSlotsByDate] = useState<Record<string, string[]>>({});
  const [isLoadingSlots, setIsLoadingSlots] = useState(false);
  const [slotsError, setSlotsError] = useState<string | null>(null);
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [selectedSlot, setSelectedSlot] = useState<string | null>(null);
  const [timeFormat, setTimeFormat] = useState<TimeFormat>('24h');
  const [bookingState, setBookingState] = useState<BookingState>('idle');
  const [bookingError, setBookingError] = useState<string | null>(null);
  const [confirmedSlot, setConfirmedSlot] = useState<string | null>(null);
  const [refreshKey, setRefreshKey] = useState(0);

  const onLoadedFiredRef = useRef(false);
  const onLoadedRef = useRef(onLoaded);
  const onBookedRef = useRef(onBooked);
  useEffect(() => {
    onLoadedRef.current = onLoaded;
    onBookedRef.current = onBooked;
  });

  const availableDates = useMemo(() => {
    const set = new Set<string>();
    for (const [date, slots] of Object.entries(slotsByDate)) {
      if (slots.length > 0) set.add(date);
    }
    return set;
  }, [slotsByDate]);

  useEffect(() => {
    const controller = new AbortController();
    setIsLoadingSlots(true);
    setSlotsError(null);

    const today = startOfToday();
    const monthStart = startOfMonth(month);
    const start = monthStart < today ? today : monthStart;
    const end = endOfMonth(month);

    getSlots({ start, end, timeZone: TIMEZONE, signal: controller.signal })
      .then(data => {
        setSlotsByDate(data);
        if (!onLoadedFiredRef.current) {
          onLoadedFiredRef.current = true;
          onLoadedRef.current?.();
        }
      })
      .catch(err => {
        if (err?.name === 'AbortError') return;
        setSlotsError(err?.message ?? 'Could not load availability.');
      })
      .finally(() => {
        if (!controller.signal.aborted) setIsLoadingSlots(false);
      });

    return () => controller.abort();
  }, [month, refreshKey]);

  const slotsForSelectedDay = useMemo(() => {
    if (!selectedDate) return [];
    return slotsByDate[format(selectedDate, 'yyyy-MM-dd')] ?? [];
  }, [selectedDate, slotsByDate]);

  const handleSelectDate = useCallback((date: Date) => {
    setSelectedDate(date);
    setSelectedSlot(null);
    setBookingError(null);
    setBookingState('idle');
  }, []);

  const handleChangeMonth = useCallback((newMonth: Date) => {
    setMonth(newMonth);
    setSelectedDate(null);
    setSelectedSlot(null);
    setBookingError(null);
    setBookingState('idle');
  }, []);

  const handleSelectSlot = useCallback((slot: string) => {
    setSelectedSlot(slot);
    setBookingError(null);
    setBookingState('idle');
  }, []);

  const handleCancelSelection = useCallback(() => {
    setSelectedSlot(null);
    setBookingError(null);
    setBookingState('idle');
  }, []);

  const handleConfirm = useCallback(async () => {
    if (!selectedSlot) return;
    setBookingState('submitting');
    setBookingError(null);
    try {
      await createBooking({
        start: selectedSlot,
        name: lead.name,
        email: lead.email,
        timeZone: TIMEZONE,
        notes: lead.message || undefined,
        metadata: {
          organisation: lead.organisation,
          role: lead.role,
        },
      });
      setConfirmedSlot(selectedSlot);
      onBookedRef.current?.();
    } catch (err) {
      if (err instanceof SlotTakenError) {
        setBookingError(err.message);
        setBookingState('error');
        setSelectedSlot(null);
        if (selectedDate) {
          setIsLoadingSlots(true);
          getSlots({
            start: startOfMonth(month) < startOfToday() ? startOfToday() : startOfMonth(month),
            end: endOfMonth(month),
            timeZone: TIMEZONE,
          })
            .then(setSlotsByDate)
            .catch(() => {})
            .finally(() => setIsLoadingSlots(false));
        }
        return;
      }
      setBookingError(err instanceof Error ? err.message : 'Booking failed. Please try again.');
      setBookingState('error');
    }
  }, [selectedSlot, lead, selectedDate, month]);

  if (confirmedSlot) {
    const startDate = new Date(confirmedSlot);
    const dateLine = new Intl.DateTimeFormat('en-GB', {
      timeZone: TIMEZONE,
      weekday: 'long',
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    }).format(startDate);
    const timeLine = new Intl.DateTimeFormat('en-GB', {
      timeZone: TIMEZONE,
      hour: 'numeric',
      minute: '2-digit',
      hour12: timeFormat === '12h',
    }).format(startDate);

    return (
      <div className="p-8 sm:p-10">
        <div className="flex flex-col items-center text-center">
          <div className="h-12 w-12 rounded-full bg-green-50 border border-green-200 flex items-center justify-center mb-5">
            <CheckCircle2 className="h-6 w-6 text-green-600" />
          </div>
          <h3 className="text-2xl font-bold text-medical-900 mb-2">You're booked in</h3>
          <p className="text-medical-600 mb-6">
            We've sent a calendar invite to <span className="font-medium text-medical-900">{lead.email}</span>.
          </p>
          <div className="w-full bg-medical-50 rounded-xl border border-medical-200/70 p-5 space-y-3 text-left">
            <div className="flex items-start gap-3">
              <Calendar className="h-4 w-4 text-medical-500 mt-0.5 flex-shrink-0" />
              <div className="text-sm">
                <p className="font-medium text-medical-900">{dateLine}</p>
                <p className="text-medical-500">{timeLine} · {EVENT_DURATION_MIN} minutes · Europe/London</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <Video className="h-4 w-4 text-medical-500 mt-0.5 flex-shrink-0" />
              <p className="text-sm text-medical-900">
                Cal Video link in your invite
              </p>
            </div>
          </div>
          <p className="text-xs text-medical-500 mt-6">
            Need to reschedule? Reply to the calendar invite or email us.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 sm:p-8">
      <div className="flex items-center gap-2 text-xs text-medical-500 mb-5">
        <Calendar className="h-3.5 w-3.5" />
        <span>{EVENT_DURATION_MIN} minutes · Europe/London</span>
      </div>

      <MonthGrid
        month={month}
        selectedDate={selectedDate}
        availableDates={availableDates}
        isLoading={isLoadingSlots}
        onSelectDate={handleSelectDate}
        onChangeMonth={handleChangeMonth}
      />

      {slotsError && (
        <div className="mt-4 text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg p-3" role="alert">
          {slotsError}
          <button
            type="button"
            onClick={() => setRefreshKey(k => k + 1)}
            className="ml-2 underline"
          >
            Retry
          </button>
        </div>
      )}

      {selectedDate && (
        <SlotList
          date={selectedDate}
          slots={slotsForSelectedDay}
          timeZone={TIMEZONE}
          timeFormat={timeFormat}
          isLoading={isLoadingSlots}
          selectedSlot={selectedSlot}
          bookingState={bookingState}
          errorMessage={bookingError}
          onChangeFormat={setTimeFormat}
          onSelectSlot={handleSelectSlot}
          onConfirm={handleConfirm}
          onCancelSelection={handleCancelSelection}
        />
      )}
    </div>
  );
};
